import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export interface Collection {
  id: string;
  user_id: string;
  name: string;
  parent_id: string | null;
  is_system: boolean;
  slug?: string;
  created_at?: string;
}

export interface CollectionTree extends Collection { 
  children: CollectionTree[];
}

type UseCollectionsOpts = {
  enabled?: boolean;   // fetch only when true
  realtime?: boolean;  // subscribe only when true
};

const TABLE = 'user_collections';

/**
 * Custom hook for managing user collections with hierarchical tree structure
 * Fetches collections from Supabase with optional realtime updates and provides
 * utilities for creating new collections and building tree hierarchies.
 *
 * @param opts - Configuration options for the hook
 * @param opts.enabled - Whether to fetch collections (default: true). Set to false to prevent fetching.
 * @param opts.realtime - Whether to subscribe to realtime updates (default: false). Enables live sync when true.
 *
 * @returns Object containing:
 *   - collections: Array<Collection> - Flat list of all collections, sorted by is_system then name
 *   - collectionsTree: Array<CollectionTree> - Hierarchical tree structure with nested children
 *   - loading: boolean - True while initial fetch is in progress
 *   - createCollection: (name: string, parentId?: string | null) => Promise<Collection> - Creates a new collection
 *   - deleteCollection: (collectionId: string) => Promise<void> - Deletes a collection by ID
 *   - renameCollection: (collectionId: string, newName: string) => Promise<void> - Renames a collection by ID
 *   - moveCollection: (collectionId: string, newParentId: string | null) => Promise<void> - Moves a collection to a new parent
 *   - refetch: () => Promise<void> - Manually refetch collections from the database
 *
 * @example
 * // Basic usage
 * const { collections, loading } = useCollections();
 *
 * @example
 * // With realtime updates and tree structure
 * const { collectionsTree, createCollection, loading } = useCollections({
 *   enabled: true,
 *   realtime: true
 * });
 *
 * // Create a new collection
 * const newCollection = await createCollection('My Collection', parentId);
 *
 * @example
 * // Conditional fetching (only fetch when user is authenticated)
 * const { collections, refetch } = useCollections({ enabled: isAuthenticated });
 *
 * @example
 * // Move a collection to a new parent
 * const { moveCollection } = useCollections();
 * await moveCollection(collectionId, newParentId);
 */
export function useCollections(opts: UseCollectionsOpts = {}) {
  const { enabled = true, realtime = false } = opts;

  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(enabled);

  const fetchCollections = useCallback(async () => {
    if (!enabled) return;
    try {
      // Rely on RLS: no need to call auth.getUser() first
      const { data, error } = await supabase
        .from(TABLE as any)
        .select('*')
        .order('is_system', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      setCollections((data as unknown as Collection[]) || []);
    } catch (err) {
      logger.error('Error fetching collections', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      setLoading(true);
      fetchCollections();
    }
  }, [enabled, fetchCollections]);

  // Realtime only when asked
  useEffect(() => {
    if (!enabled || !realtime) return;
    const channel = supabase
      .channel('user_collections_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLE }, fetchCollections)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [enabled, realtime, fetchCollections]);

  const buildTree = useCallback((items: Collection[]): CollectionTree[] => {
    const map = new Map<string, CollectionTree>();
    const roots: CollectionTree[] = [];
    items.forEach(i => map.set(i.id, { ...i, children: [] }));
    items.forEach(i => {
      const n = map.get(i.id)!;
      if (i.parent_id && map.has(i.parent_id)) map.get(i.parent_id)!.children.push(n);
      else roots.push(n);
    });
    const sortFn = (a: CollectionTree, b: CollectionTree) =>
      (a.is_system === b.is_system ? a.name.localeCompare(b.name) : (a.is_system ? -1 : 1));
    const sortTree = (nodes: CollectionTree[]) => { nodes.sort(sortFn); nodes.forEach(n => sortTree(n.children)); };
    sortTree(roots);
    return roots;
  }, []);

  const collectionsTree = useMemo(() => buildTree(collections), [collections, buildTree]);

  const createCollection = useCallback(async (name: string, parentId: string | null = null) => {
    const clean = name.trim();
    if (!clean) throw new Error('Collection name required');
    // get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from(TABLE as any)
      .insert({ name: clean, parent_id: parentId, is_system: false , user_id: user.id })
      .select('*')
      .single();
    if (error) throw error;
    setCollections(prev => [data as unknown as Collection, ...prev]);
    return data as unknown as Collection;
  }, []);

  const deleteCollection = useCallback(async (collectionId: string) => {
    if (!collectionId) throw new Error('Collection ID required');

    try {
      const { error } = await supabase
        .from(TABLE as any)
        .delete()
        .eq('id', collectionId);

      if (error) throw error;

      setCollections(prev => prev.filter(c => c.id !== collectionId));
    } catch (e) {
      logger.error('Error deleting collection', e, { collectionId });
      throw e;
    }
  }, []);

  const renameCollection = useCallback(async (collectionId: string, newName: string) => {
    if (!collectionId) throw new Error('Collection ID required');
    const clean = newName.trim();
    if (!clean) throw new Error('Collection name required');

    try {
      const { error } = await supabase
        .from(TABLE as any)
        .update({ name: clean })
        .eq('id', collectionId);

      if (error) throw error;

      setCollections(prev => prev.map(c => c.id === collectionId ? { ...c, name: clean } : c));
    } catch (e) {
      logger.error('Error renaming collection', e, { collectionId, newName: clean });
      throw e;
    }
  }, []);

  const moveCollection = useCallback(async (collectionId: string, newParentId: string | null) => {
    if (!collectionId) throw new Error('Collection ID required');

    try {
      const { error } = await supabase
        .from(TABLE as any)
        .update({ parent_id: newParentId })
        .eq('id', collectionId);

      if (error) throw error;

      setCollections(prev => prev.map(c => c.id === collectionId ? { ...c, parent_id: newParentId } : c));
    } catch (e) {
      console.error('moveCollection error', e);
      throw e;
    }
  }, []);

  return { collections, collectionsTree, loading, createCollection, deleteCollection, renameCollection, moveCollection, refetch: fetchCollections };
}
